import { useState, useEffect, useRef } from 'react';
import { Check, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StreakCounter } from './StreakCounter';
import { HabitLinkedAction } from '@/types/goal';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface HabitCheckboxProps {
  title: string;
  completed: boolean;
  streak: number;
  onToggle: () => void;
  linkedAction?: HabitLinkedAction;
  onNavigateToPlan?: (milestoneIndex: number) => void;
}

export const HabitCheckbox = ({ 
  title, 
  completed, 
  streak, 
  onToggle,
  linkedAction,
  onNavigateToPlan,
}: HabitCheckboxProps) => {
  const [justCompleted, setJustCompleted] = useState(false);
  const [showGlow, setShowGlow] = useState(false);
  const [checkmarkProgress, setCheckmarkProgress] = useState(completed ? 100 : 0);
  const [streakBump, setStreakBump] = useState(false);
  const prevCompleted = useRef(completed);
  const prevStreak = useRef(streak);

  // Handle completion animations
  useEffect(() => {
    // Detect transition from uncompleted to completed
    if (completed && !prevCompleted.current) {
      setJustCompleted(true);
      setShowGlow(true);
      setCheckmarkProgress(100);
      
      // Check if streak increased
      if (streak > prevStreak.current) {
        setStreakBump(true);
        setTimeout(() => setStreakBump(false), 300);
      }

      // Remove glow after animation
      const glowTimer = setTimeout(() => setShowGlow(false), 300);
      const completeTimer = setTimeout(() => setJustCompleted(false), 500);
      
      return () => {
        clearTimeout(glowTimer);
        clearTimeout(completeTimer);
      };
    } else if (!completed && prevCompleted.current) {
      // Unchecking
      setCheckmarkProgress(0);
    }
    
    prevCompleted.current = completed;
    prevStreak.current = streak;
  }, [completed, streak]);

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (linkedAction && onNavigateToPlan) {
      onNavigateToPlan(linkedAction.milestoneIndex);
    }
  };

  return (
    <div
      className={cn(
        'rounded-lg border transition-all duration-200 relative',
        completed
          ? 'bg-success/10 border-success/30'
          : 'bg-card border-border hover:border-primary/30 hover:bg-secondary/50',
        showGlow && 'shadow-[0_0_20px_rgba(34,197,94,0.4)] border-success'
      )}
    >
      {/* Spark particles on completion */}
      {justCompleted && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-lg">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-success rounded-full animate-spark"
              style={{
                left: '24px',
                top: '50%',
                animationDelay: `${i * 50}ms`,
                '--spark-x': `${(Math.random() - 0.5) * 60}px`,
                '--spark-y': `${(Math.random() - 0.5) * 40}px`,
              } as React.CSSProperties}
            />
          ))}
        </div>
      )}

      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full p-4"
      >
        <div className="flex items-center gap-3">
          {/* Animated Checkbox */}
          <div
            className={cn(
              'w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0 relative overflow-hidden',
              completed
                ? 'border-success'
                : 'border-muted-foreground/40 hover:border-primary',
              justCompleted && 'animate-checkbox-pop'
            )}
          >
            {/* Gradient background fill */}
            <div 
              className={cn(
                "absolute inset-0 bg-gradient-to-br from-success to-emerald-600 transition-transform duration-200 origin-center",
                completed ? "scale-100" : "scale-0"
              )}
            />
            
            {/* Animated Checkmark SVG */}
            {completed && (
              <svg
                className="w-4 h-4 relative z-10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path
                  d="M5 12l5 5L19 7"
                  className={cn(
                    "transition-all duration-200",
                    justCompleted && "animate-checkmark-draw"
                  )}
                  style={{
                    strokeDasharray: 24,
                    strokeDashoffset: justCompleted ? 0 : (100 - checkmarkProgress) * 0.24,
                  }}
                />
              </svg>
            )}
          </div>

          {/* Title with strikethrough animation */}
          <span
            className={cn(
              'font-medium transition-all text-left relative',
              completed && 'text-muted-foreground'
            )}
          >
            {title}
            {/* Animated strikethrough */}
            <span 
              className={cn(
                "absolute left-0 top-1/2 h-[2px] bg-muted-foreground/80 transition-all duration-200 ease-out",
                completed ? "w-full" : "w-0"
              )}
              style={{ transform: 'translateY(-50%)' }}
            />
          </span>
        </div>

        {/* Animated Streak Counter */}
        <div className={cn(streakBump && "animate-streak-bump")}>
          <StreakCounter 
            count={streak} 
            animate={justCompleted} 
            showSparks={justCompleted && streak > prevStreak.current}
          />
        </div>
      </button>
      
      {/* Linked Action Info */}
      {linkedAction && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleLinkClick}
                className={cn(
                  "flex items-center gap-1.5 px-4 pb-3 pt-0 text-[13px] text-muted-foreground hover:text-primary transition-colors w-full text-left group",
                  !onNavigateToPlan && "cursor-default"
                )}
              >
                <MapPin className="w-3.5 h-3.5 flex-shrink-0 group-hover:text-primary" />
                <span className="truncate">
                  Contributing to: {truncateText(linkedAction.milestoneName, 30)}
                </span>
              </button>
            </TooltipTrigger>
            <TooltipContent 
              side="bottom" 
              align="start"
              className="max-w-[300px] bg-popover border-border"
            >
              <div className="space-y-1">
                <p className="font-medium text-sm">{linkedAction.milestoneName}</p>
                <p className="text-xs text-muted-foreground">{linkedAction.actionText}</p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};
